import React from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineFire, HiOutlinePhoto } from "react-icons/hi2";

/**
 * Carte de recette pour la communauté
 * Affiche les recettes populaires avec leurs statistiques
 * Si onRecipeClick est fourni, le clic ouvre le popup ; sinon navigation vers /recipe/:id
 */
const RecipeCard = ({ recipe, user, onRecipeClick }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = React.useState(false);

  const handleClick = () => {
    if (onRecipeClick) {
      onRecipeClick(recipe);
    } else {
      navigate(`/recipe/${recipe.id}`);
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      Facile: "bg-green-100 text-green-800",
      Moyen: "bg-yellow-100 text-yellow-800",
      Difficile: "bg-red-100 text-red-800",
    };
    return colors[difficulty] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    return date.toLocaleDateString("fr-FR");
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="text-yellow-400">
            ★
          </span>,
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <span key={i} className="text-yellow-400">
            ★
          </span>,
        );
      } else {
        stars.push(
          <span key={i} className="text-gray-300">
            ★
          </span>,
        );
      }
    }
    return stars;
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white dark:bg-ink-800 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer border border-slate-200/80 dark:border-ink-700 group touch-manipulation"
    >
      {/* Image recette ou placeholder - hauteur réduite sur mobile */}
      <div className="h-40 sm:h-48 bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 relative overflow-hidden">
        {recipe.image_url && !imageError ? (
          <>
            <img
              src={recipe.image_url}
              alt={recipe.title || "Recette"}
              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
              onError={() => setImageError(true)}
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-all duration-200" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/60">
            <HiOutlinePhoto className="w-20 h-20 shrink-0" />
          </div>
        )}

        {/* Badge de popularité */}
        {recipe.popularity_score > 50 && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-yellow-400 text-yellow-900 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-lg flex items-center gap-1 sm:gap-1.5">
            <HiOutlineFire className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
            <span>Populaire</span>
          </div>
        )}
      </div>

      <div className="p-3 sm:p-5">
        {/* Titre et difficulté */}
        <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-white line-clamp-2 flex-1 min-w-0">
            {recipe.title}
          </h3>
          <span
            className={`flex-shrink-0 ml-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${getDifficultyColor(
              recipe.difficulty,
            )}`}
          >
            {recipe.difficulty}
          </span>
        </div>

        {/* Description */}
        {recipe.description && (
          <p className="text-xs sm:text-sm text-slate-600 dark:text-ink-300 line-clamp-2 mb-3 sm:mb-4">
            {recipe.description}
          </p>
        )}

        {/* Auteur */}
        <div className="flex items-center gap-2 mb-3 sm:mb-4 text-xs sm:text-sm text-slate-500 dark:text-ink-400 min-w-0">
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center text-white text-[10px] sm:text-xs font-bold flex-shrink-0">
            {recipe.first_name?.[0] || recipe.username?.[0]}
          </div>
          <span className="font-medium truncate">
            {recipe.first_name && recipe.last_name
              ? `${recipe.first_name} ${recipe.last_name}`
              : recipe.username}
          </span>
          <span className="text-slate-400 dark:text-ink-500 flex-shrink-0">•</span>
          <span className="flex-shrink-0">{formatDate(recipe.shared_at || recipe.created_at)}</span>
        </div>

        {/* Statistiques */}
        <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-slate-100 dark:border-ink-700 gap-2">
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm min-w-0">
            <div className="flex items-center space-x-1 text-red-500">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{recipe.favorites_count || 0}</span>
            </div>

            <div className="flex items-center space-x-1 text-pine-600">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              <span className="font-medium">{recipe.comments_count || 0}</span>
            </div>
          </div>

          {/* Note moyenne */}
          {recipe.average_rating > 0 && (
            <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
              {renderStars(Number(recipe.average_rating))}
              <span className="text-xs sm:text-sm font-medium text-slate-700 dark:text-ink-200 tabular-nums">
                {Number(recipe.average_rating).toFixed(1)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;
